import { useState, useEffect } from "react";
import axiosInstance from "../../API/interceptor";
import { PhotoAvatar } from "./PersonCell";

const RACI_LABEL = { R: "Responsible", A: "Accountable", C: "Consulted", I: "Informed" };
const ROLE_MAP   = { R: "responsible", A: "accountable", C: "consulted",  I: "informed"  };


const WS_COLORS = [
  { color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
  { color: "#0D9488", bg: "#F0FDFA", border: "#99F6E4" },
  { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
  { color: "#D97706", bg: "#FFF7ED", border: "#FDE68A" },
  { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
  { color: "#DB2777", bg: "#FDF2F8", border: "#F9A8D4" },
];

export default function PersonTasksPopup({
  name, personId, activeRaci,
  raciLeft, raciWidth, raciTop,
  cancelHide, scheduleHide,
}) {
  const [breakdown,  setBreakdown]  = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    if (!personId || !activeRaci) return;
    setLoading(true);
    setBreakdown([]);

    axiosInstance
      .post("/task_assignment/get_person_product_raci_breakdown", {
        person_id: personId,
        raci_role: ROLE_MAP[activeRaci] || null,
      })
      .then(res => {
        const data = Array.isArray(res.data?.breakdown) ? res.data.breakdown : [];
        setBreakdown(data);
        setTotalCount(data.reduce((s, w) => s + (w.count || 0), 0));
      })
      .catch(() => { setBreakdown([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [personId, activeRaci]);

  const POPUP_W    = 296, GAP = 12;
  const spaceRight = window.innerWidth - (raciLeft + raciWidth + GAP);
  const left       = spaceRight >= POPUP_W ? raciLeft + raciWidth + GAP : Math.max(8, raciLeft - POPUP_W - GAP);
  const top        = Math.min(raciTop, Math.max(8, window.innerHeight - 380));

  return (
    <div
      onMouseEnter={cancelHide}
      onMouseLeave={scheduleHide}
      className="fixed z-[2147483647] bg-white rounded-2xl border border-[#E8E4F8] overflow-hidden shadow-[0_24px_64px_rgba(109,40,217,.2),0_4px_16px_rgba(0,0,0,.1)] w-[296px]"
      style={{ left, top }}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-[linear-gradient(135deg,#7C3AED,#6D28D9)]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <PhotoAvatar name={name} userId={personId} size={36} />
            <div>
              <div className="text-sm font-black text-white">
                {RACI_LABEL[activeRaci]} Tasks — {name}
              </div>
              <div className="text-[10.5px] text-white/60 mt-0.5">Workspace breakdown</div>
            </div>
          </div>
          <div className="px-3 py-1 rounded-full flex items-center gap-1 bg-white/[.18] border border-white/[.22]">
            <span className="text-xs font-bold text-white">{totalCount} tasks</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1 h-1">
          {breakdown.map((w, i) => (
            <div
              key={i}
              className={`rounded-sm min-w-1.5 ${i === 0 ? "bg-white/90" : "bg-white/45"}`}
              style={{ flex: w.count }}
            />
          ))}
        </div>
      </div>

      {/* Workspace list */}
      <div className="p-2.5 flex flex-col gap-1.5 max-h-64 overflow-y-auto">
        {loading ? (
          <p className="text-xs text-slate-400 text-center py-4 m-0">Loading...</p>
        ) : breakdown.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4 m-0">No tasks found</p>
        ) : breakdown.map((w, i) => {
          const wc = WS_COLORS[i % WS_COLORS.length];
          const wsName = w.workspace || w.product_name || "Unknown";
          return (
            <div
              key={w.product_code || i}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-default transition-transform hover:translate-x-1 border-[1.5px]"
              style={{ background: wc.bg, borderColor: wc.border }}
            >
              <div className="w-1 h-10 rounded-sm flex-shrink-0" style={{ background: wc.color }} />
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-800">{wsName}</div>
                <div className="text-[10.5px] text-slate-400 mt-0.5">
                  {w.count} task{w.count !== 1 ? "s" : ""} assigned
                </div>
              </div>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0"
                style={{ background: wc.color }}
              >
                {w.count}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-3.5 py-2.5 border-t border-gray-100 flex justify-between items-center bg-slate-50">
        <span className="text-xs text-slate-400">Total assigned tasks</span>
        <span className="text-sm font-black text-violet-600">{totalCount}</span>
      </div>
    </div>
  );
}